import {Exclude, Expose, instanceToPlain, plainToInstance, Transform, Type} from "class-transformer";
import * as t from "io-ts";
import "reflect-metadata";
import {validateJSON} from "../web/validation";
import {GeoCoord} from "./geo-coord";
import {DispatchRequest, DispatchRequestEntity, DispatchRequestValidator} from "./dispatch-request";
import {LocatableEntity} from "./entity";
import {DatabaseCode, toDatabaseCode} from "./database-code";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";
import {CargoSubtype, toCargoSubtype} from "./cargo-subtype";
import {Meter, MeterEntity} from "./meter";
import {Restriction, RestrictionArrayValidator, RestrictionEntity} from "./restriction";
import {ObjectPreference, ObjectPreferenceArrayCodec, ObjectPreferenceEntity} from "./object-preference";
import {WaterGoPreference, WaterGoPreferenceCodec, WaterGoPreferenceEntity} from "./water-go-preference";
import {OilGoPreference, OilGoPreferenceCodec, OilGoPreferenceEntity} from "./oil-go-preference";
import {PatchValue} from "./patch-value";
import {ScadaMode, toScadaMode} from "./scada-mode";


export interface ContainerConfigurable {
  id: string
  name?: string
  siteId?: string
  siteName?: string
  siteCompanyId?: string
  siteCompanyName?: string
  scadaMode?: ScadaMode
  scadaModeReason?: string
  active?: boolean
  dispatchIgnoreMaxNonPrimaryVolume?: boolean;
  doNotDispatch?: boolean
  clearDoNotDispatchScheduledDttm?: number
  clearDoNotDispatchById?: string
  clearDoNotDispatchNote?: string
  clearDoNotDispatchDttm?: number
  doNotUse?: boolean;
  clearDoNotUseScheduledDttm?: number;
  clearDoNotUseById?: string
  clearDoNotUseNote?: string
  clearDoNotUseDttm?: number
  siteTransportViaPipeline?: boolean
  siteTransportOilViaPipeline?: boolean
  lactMeters?: MeterEntity[]
  restrictions?: RestrictionEntity[]
  cargoSubtype?: CargoSubtype
}

export interface ContainerInterface {
  id: string;
  externalId: string;
  abbr?: string;
  nextDispatchRequest?: DispatchRequest;
  owningCompanyId?: string;
  owningCompanyName?: string;
  barCode?: string;
  latCoord?: number;
  longCoord?: number;
  siteName?: string;
  waterTypeId?: string;
  waterTypeName?: string;
  waterTypeAbbr?: string;
  height?: number;
  fillRate?: number;
  pullTime?: number;
  type?: string;
  leaseId?: string;
  leaseName?: string;
  afe?: string;
  afePrefix?: string;
  afeSuffix?: string;
  scadaMode?: string;
  scadaModeReason?: string;
  weight?: number;
  weightUnitOfMeasure?: string;
  diameter?: number;
  diamterUnitOfMeasure?: string;
  sizeDecimal?: number;
  sizeUnitOfMeasure?: string;
  waterTypeCargoSubtype?: string;
  totalPercent?: number;
  totalFillHeight?: number;
  totalFillDttm?: number;
  showGauges: boolean;
  forceGauges: boolean;
  showOilGauges: boolean;
  forceOilGauges: boolean;
  forceFlowMeter: boolean;
  moveable?: boolean;
  volumeTableId?: string;
  createDttm?: number;
  lastModifiedDttm?: number;
  regionList?: string[];
  containerClusterId?: string
  siteId?: string
  siteCompanyId?: string
  siteCompanyName?: string
  active?: boolean
  dispatchIgnoreMaxNonPrimaryVolume?: boolean;
  doNotDispatch?: boolean
  clearDoNotDispatchScheduledDttm?: number
  clearDoNotDispatchById?: string
  clearDoNotDispatchNote?: string
  clearDoNotDispatchDttm?: number
  doNotUse?: boolean;
  clearDoNotUseScheduledDttm?: number;
  clearDoNotUseById?: string
  clearDoNotUseNote?: string
  clearDoNotUseDttm?: number
  siteTransportViaPipeline?: boolean
  siteTransportOilViaPipeline?: boolean
  lactMeters?: Meter[]
  restrictions?: Restriction[]
  objectPreferences?: ObjectPreference[],
  waterGoPreferences?: WaterGoPreference,
  oilGoPreference?: OilGoPreference,
}

export const ContainerCodec: t.Type<ContainerInterface> = t.recursion(
  "DispatchRequest",
  () =>
    t.intersection([
      t.type({
        id: t.string,
        externalId: t.string,
        showGauges: t.boolean,
        forceGauges: t.boolean,
        showOilGauges: t.boolean,
        forceOilGauges: t.boolean,
        forceFlowMeter: t.boolean,
      }),
      t.partial({
        afe: t.string,
        afePrefix: t.string,
        afeSuffix: t.string,
        active: t.boolean,
        abbr: t.string,
        nextDispatchRequest: DispatchRequestValidator,
        owningCompanyId: t.string,
        owningCompanyName: t.string,
        barCode: t.string,
        latCoord: t.number,
        longCoord: t.number,
        siteId: t.string,
        siteName: t.string,
        siteTransportViaPipeline: t.boolean,
        siteTransportOilViaPipeline: t.boolean,
        siteCompanyId: t.string,
        siteCompanyName: t.string,
        waterTypeId: t.string,
        waterTypeName: t.string,
        waterTypeAbbr: t.string,
        scadaMode: t.string,
        scadaModeReason: t.string,
        leaseId: t.string,
        leaseName: t.string,
        weight: t.number,
        weightUnitOfMeasure: t.string,
        height: t.number,
        diameter: t.number,
        diamterUnitOfMeasure: t.string,
        fillRate: t.number,
        pullTime: t.number,
        type: t.string,
        waterTypeCargoSubtype: t.string,
        totalPercent: t.number,
        totalFillHeight: t.number,
        totalFillDttm: t.number,
        moveable: t.boolean,
        volumeTableId: t.string,
        containerClusterId: t.string,
        containerClusterName: t.string,
        dispatchIgnoreMaxNonPrimaryVolume: t.boolean,
        restrictions: RestrictionArrayValidator,
        doNotDispatch: t.boolean,
        clearDoNotDispatchScheduledDttm: t.number,
        clearDoNotDispatchById: t.string,
        clearDoNotDispatchNote: t.string,
        clearDoNotDispatchDttm: t.number,
        doNotUse: t.boolean,
        clearDoNotUseScheduledDttm: t.number,
        clearDoNotUseById: t.string,
        clearDoNotUseNote: t.string,
        clearDoNotUseDttm: t.number,
        createDttm: t.number,
        lastModifiedDttm: t.number,
        regionList: t.array(t.string),

        objectPreferences: ObjectPreferenceArrayCodec,
        waterGoPreferences: WaterGoPreferenceCodec,
        oilGoPreference: OilGoPreferenceCodec,
      })
    ])
);

export const ContainerArrayCodec = t.array(ContainerCodec);

export type Container = t.TypeOf<typeof ContainerCodec>;
export type ContainerArray = t.TypeOf<typeof ContainerArrayCodec>;

/**
 * The type of container.
 */
export interface ContainerType extends DatabaseCode {
}

const skeleton: ContainerType = {
  code: "SKELETON",
  displayValue: {
    id: "ots.ui.entity.container.type.skeleton",
    defaultMessage: "Skeleton"
  }
};

const commingled: ContainerType = {
  code: "COMMINGLED",
  displayValue: {
    id: "ots.ui.entity.container.type.commingled",
    defaultMessage: "Commingled"
  }
};

const singleTrain: ContainerType = {
  code: "SINGLE_TRAIN",
  displayValue: {
    id: "ots.ui.entity.container.type.singletrain",
    defaultMessage: "Single Train"
  }
};

const loadPoint: ContainerType = {
  code: "LOAD_POINT",
  displayValue: {
    id: "ots.ui.entity.container.type.loadpoint",
    defaultMessage: "Load Point"
  }
};

export const containerType = {
  skeleton,
  commingled,
  singleTrain,
  loadPoint
};

export const containerTypes = Object.values(containerType);

export const toContainerType = (code: string): ContainerType => toDatabaseCode(code, containerTypes);

/**
 * A container.
 */
@Exclude()
export class ContainerEntity implements LocatableEntity, ContainerConfigurable {
  @Expose()
  id!: string;

  @Expose({name: "externalId"})
  name!: string;

  @Expose()
  abbr?: string;

  @Transform(getDatabaseCodeTransformer(toContainerType))
  @Expose()
  type?: ContainerType;

  @Transform(getDatabaseCodeTransformer(toCargoSubtype))
  @Expose({name: 'waterTypeCargoSubtype'})
  cargoSubtype?: CargoSubtype;

  @Expose()
  owningCompanyId?: string;

  @Expose()
  owningCompanyName?: string;

  @Expose({name: "barCode"})
  barcode?: string;

  @Expose()
  afe?: string

  @Expose()
  afePrefix?: string

  @Expose()
  afeSuffix?: string

  @Expose()
  leaseId?: string

  @Expose()
  leaseName?: string

  @Expose()
  active?: boolean;

  @Expose()
  siteId?: string;

  @Expose()
  siteName?: string;

  @Expose()
  siteTransportViaPipeline?: boolean

  @Expose()
  siteTransportOilViaPipeline?: boolean

  @Expose()
  siteCompanyId?: string

  @Expose()
  siteCompanyName?: string

  @Expose()
  waterTypeId?: string;

  @Expose()
  waterTypeName?: string;

  @Expose()
  waterTypeAbbr?: string;

  @Transform(getDatabaseCodeTransformer(toScadaMode))
  @Expose()
  scadaMode?: ScadaMode;

  @Expose()
  scadaModeReason?: string;

  @Expose({name: "sizeDecimal"})
  size?: number;

  @Expose({name: "sizeUnitOfMeasure"})
  sizeUom?: string;

  @Expose()
  weight?: number;

  @Expose({name: "weightUnitOfMeasure"})
  weightUom?: string;

  @Expose()
  height?: number;

  @Expose()
  diameter?: number;

  @Expose({name: "diamterUnitOfMeasure"})
  diameterUom?: string;

  @Expose()
  fillRate?: number;

  @Expose()
  pullTime?: number;

  @Expose({name: "showGauges"})
  showWaterGauges = true;

  @Expose({name: "forceGauges"})
  forceWaterGauges = false;

  @Expose({name: "forceFlowMeter"})
  forceMeterReading = false;

  @Expose()
  showOilGauges = false;

  @Expose()
  forceOilGauges = false;

  @Expose()
  moveable = false;

  @Expose()
  volumeTableId?: string;

  @Expose()
  containerClusterId?: string;

  @Expose()
  containerClusterName?: string;

  @Expose()
  private totalPercent?: number;

  @Expose()
  private totalFillHeight?: number;

  @Expose()
  totalFillDttm?: number;

  @Expose()
  private latCoord?: number;

  @Expose()
  private longCoord?: number;

  @Expose()
  dispatchIgnoreMaxNonPrimaryVolume = false

  @Expose()
  doNotDispatch?: boolean;

  @Expose()
  clearDoNotDispatchScheduledDttm?: number

  @Expose()
  clearDoNotDispatchById?: string

  @Expose()
  clearDoNotDispatchNote?: string

  @Expose()
  clearDoNotDispatchDttm?: number

  @Expose()
  doNotUse = false;

  @Expose()
  clearDoNotUseScheduledDttm?: number

  @Expose()
  clearDoNotUseById?: string

  @Expose()
  clearDoNotUseNote?: string

  @Expose()
  clearDoNotUseDttm?: number

  @Expose({name: 'regionList'})
  regionIds?: string[]

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  @Expose()
  @Type(() => DispatchRequestEntity)
  nextDispatchRequest?: DispatchRequestEntity;

  @Expose()
  @Type(() => RestrictionEntity)
  restrictions?: RestrictionEntity[]

  @Expose()
  @Type(() => ObjectPreferenceEntity)
  objectPreferences?: ObjectPreferenceEntity[]

  @Expose()
  @Type(() => WaterGoPreferenceEntity)
  waterGoPreference?: WaterGoPreferenceEntity

  @Expose()
  @Type(() => OilGoPreferenceEntity)
  oilGoPreferenceEntity?: OilGoPreferenceEntity

  readonly entityType = "CONTAINER";

  get displayName(): Readonly<string> {
    return this.name;
  }

  get latLng(): Readonly<GeoCoord | undefined> {
    return this.location;
  }

  /**
   * Constructor for ContainerEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  /**
   * Create a ContainerEntity from a Container type value.
   *
   * @param container The container type value.
   */
  static from(container: Container): ContainerEntity {
    return plainToInstance(ContainerEntity, container, {
      excludeExtraneousValues: true
    });
  }

  /**
   * Deserializes the ContainerEntity to a Container type value.
   * If validation fails, the promise will be rejected.
   */
  toTypeValue(): Promise<Container> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true
    });
    return validateJSON(ContainerCodec, object);
  }

  get location(): GeoCoord | undefined {
    if (this.latCoord && this.longCoord) {
      return {
        latCoord: this.latCoord,
        longCoord: this.longCoord
      };
    } else {
      return undefined;
    }
  }

  get percentFilled(): number | undefined {
    return this.totalPercent;
  }

  get fillHeightInInches(): number | undefined {
    return this.totalFillHeight;
  }

  getDisplayableName = (useAbbreviation: boolean | undefined): string => {
    if (useAbbreviation) {
      return this.abbr ?? this.name;
    } else {
      return this.name;
    }
  };

  get abacAttributes(): Record<string, any> | undefined {
    return {
      id: this.id,
      name: this.name,
      siteId: this.siteId,
      siteName: this.siteName,
      siteCompanyId: this.siteCompanyId,
      siteCompanyName: this.siteCompanyName,
      companyId: this.owningCompanyId,
      companyName: this.owningCompanyName,
    }
  }
}

export interface ContainerSearchCriteria {
  ids?: string[]
  regionIds?: string[];
  siteIds?: string[];
  siteCompanyIds?: string[];
  cargoSubtypes?: string[];
  withDefaults?: boolean
  active?: boolean
}

export interface ContainerExportOptions {
  regionIds?: string[];
  companyIds?: string[];
  wellMappingKey?: string;
  containerMappingKey?: string;
  containerMappingKey2?: string;
}

export const CONTAINER_PATCH_FIELD_MAP: Record<string, keyof ContainerPatchDTO> = {
  externalId: 'name',
  abbr: 'abbr',
  barCode: 'barcode',
  leaseId: 'leaseId',
  leaseName: 'leaseName',
  afe: 'afe',
  afePrefix: 'afePrefix',
  afeSuffix: 'afeSuffix',
  type: 'type',
  owningCompanyId: 'owningCompanyId',
  waterTypeId: 'freightTypeId',
  siteId: 'siteId',
  containerClusterId: 'containerClusterId',
  sizeDecimal: 'size',
  sizeUnitOfMeasure: 'sizeUnitOfMeasure',
  weight: 'weight',
  weightUnitOfMeasure: 'weightUnitOfMeasure',
  height: 'height',
  diameter: 'diameter',
  diamterUnitOfMeasure: 'diameterUnitOfMeasure',
  scadaMode: 'scadaMode',
  scadaModeReason: 'scadaModeReason',
  dispatchIgnoreMaxNonPrimaryVolume: 'dispatchIgnoreMaxNonPrimaryVolume',
  doNotDispatch: 'doNotDispatch',
  doNotUse: 'doNotUse',
  active: 'active',
};

export interface ContainerPatchDTO {
  name?: PatchValue<string>
  abbr?: PatchValue<string>
  barcode?: PatchValue<string>
  leaseId?: PatchValue<string>
  leaseName?: PatchValue<string>
  afe?: PatchValue<string>
  afePrefix?: PatchValue<string>
  afeSuffix?: PatchValue<string>
  type?: PatchValue<string>
  owningCompanyId?: PatchValue<string>
  freightTypeId?: PatchValue<string>
  siteId?: PatchValue<string>
  containerClusterId?: PatchValue<string>
  size?: PatchValue<number>
  sizeUnitOfMeasure?: PatchValue<string>
  weight?: PatchValue<number>
  weightUnitOfMeasure?: PatchValue<string>
  height?: PatchValue<number>
  diameter?: PatchValue<number>
  diameterUnitOfMeasure?: PatchValue<string>
  scadaMode?: PatchValue<string>
  scadaModeReason?: PatchValue<string>
  dispatchIgnoreMaxNonPrimaryVolume?: PatchValue<boolean>
  doNotDispatch?: PatchValue<boolean>
  doNotUse?: PatchValue<boolean>
  active?: PatchValue<boolean>
}
